<template>
    <div class="option-card">
        <div class="card-padding">
            <!-- Row 1: Name + Dégressif link + Action buttons -->
            <div class="option-top-row">
                <div class="option-name-col">
                    <div v-if="!isEditingName" class="editable-label">
                        <span class="label_name_tarif">{{ tarif.name || `Option ${optionIndex}` }}</span>
                        <SquarePen class="edit-pen-icon" :size="12" @click="isEditingName = true" />
                    </div>
                    <div v-else class="editable-input">
                        <CustomInput
                            v-model="tarif.name"
                            type="text"
                            @blur="isEditingName = false"
                            @enter="isEditingName = false"
                            @input="$emit('focus')"
                            classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                            classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                            :customSizeLabel="10"
                            customClassLabel="poppinsMedium"
                        />
                    </div>
                    <span
                        class="degressif-link"
                        :class="{ 'degressif-link--disabled': isDegressifDisabled }"
                        @click="toggleDegressif"
                    >
                        {{ $t('experience.tiers.configurePricingLink') }}
                        <ChevronUp :size="8" v-if="isDegressifOpen && !isDegressifDisabled" />
                        <ChevronDown :size="8" v-else-if="!isDegressifDisabled" />
                    </span>
                </div>

                <!-- Row 1 right: Duration + Price + Actions (all inline) -->
                <div class="option-inputs-row">
                    <div class="input-group">
                        <div v-if="Number(selected_duration_experience) !== -1" class="input-item input-item--duration">
                            <BaseSelect
                                v-model="selected_duration_experience"
                                label="Durée"
                                :required="true"
                                :options="filteredOptionsDurations"
                                :searchable="false"
                                option-label="label"
                                option-value="value"
                                class="transparent-select ppv2-w-full"
                                class-added="!ppv2-rounded-[4px] !ppv2-h-[30px] !ppv2-min-h-[30px]"
                                customInputClass="!ppv2-opacity-100"
                                :customSizeLabel="11"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                :error="errors.duree"
                                @input="
                                    clearError('duree')
                                    $emit('focus')
                                "
                                @open="$emit('focus')"
                            />
                        </div>

                        <div v-else class="input-item input-item--duration">
                            <div class="custom-duration-wrapper">
                                <CustomInput
                                    v-model="tarif.duree"
                                    label="Durée perso."
                                    type="number"
                                    :min="1"
                                    :hideValidIcon="true"
                                    :state="errors.duree ? 'error' : 'default'"
                                    :errorText="errors.duree"
                                    @input="
                                        clearError('duree')
                                        $emit('focus')
                                    "
                                    classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg !ppv2-pr-7"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                    :customSizeLabel="10"
                                    customClassLabel="poppinsMedium"
                                    :floatingLabel="true"
                                    :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                />
                                <button type="button" class="clear-custom-btn" @click="clearCustomDuration">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M8.33068 4.16406C7.91658 4.16409 7.51732 4.31826 7.21068 4.59656L1.93735 9.3799C1.85128 9.45801 1.78251 9.55326 1.73544 9.65953C1.68838 9.76581 1.66406 9.88075 1.66406 9.99698C1.66406 10.1132 1.68838 10.2282 1.73544 10.3344C1.78251 10.4407 1.85128 10.536 1.93735 10.6141L7.21068 15.3982C7.51732 15.6765 7.91658 15.8307 8.33068 15.8307H16.664C17.106 15.8307 17.53 15.6551 17.8425 15.3426C18.1551 15.03 18.3307 14.6061 18.3307 14.1641V5.83073C18.3307 5.3887 18.1551 4.96478 17.8425 4.65222C17.53 4.33966 17.106 4.16406 16.664 4.16406H8.33068Z"
                                            stroke="#FF8686"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M10 7.5L15 12.5"
                                            stroke="#FF8686"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                        <path
                                            d="M15 7.5L10 12.5"
                                            stroke="#FF8686"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div class="input-item input-item--price">
                            <CustomInput
                                v-model="tarif.price"
                                :label="optionPriceLabel"
                                :right-icon="$symbolCurrency"
                                :required="true"
                                :state="getState('price')"
                                :errorText="errors.price"
                                type="number"
                                :min="0"
                                @input="
                                    clearError('price')
                                    $emit('focus')
                                "
                                classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                :customSizeLabel="10"
                                customClassLabel="poppinsMedium"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                            />
                        </div>
                    </div>

                    <div class="action-btns">
                        <button type="button" class="action-icon-btn" @click="$emit('duplicate')">
                            <Copy :size="14" />
                        </button>
                        <button type="button" class="action-icon-btn" @click="$emit('remove')">
                            <X :size="14" />
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Paliers section -->
        <div v-if="isDegressifOpen" class="paliers-section">
            <!-- Même rappel qu'à l'étape Disponibilités : les paliers sont la même donnée des deux côtés. -->
            <p v-if="hasDynamicDuration" class="palier-fallback-hint card-padding">
                {{ $t('experience.tiers.fallbackHint') }}
            </p>
            <div v-for="(price_degresif, index_degresif) in tarif.prices_degressif" :key="index_degresif">
                <div class="card-divider"></div>
                <div class="palier-row card-padding">
                    <div class="palier-label poppinsMedium">{{ $t('experience.tiers.palier') }} {{ index_degresif + 1 }}</div>

                    <div class="palier-inputs-row">
                        <div class="palier-inputs">
                            <div class="palier-field palier-field--duration">
                                <CustomInput
                                    v-model="price_degresif.persons"
                                    :label="$t('experience.step5.reservationMode.nbrParticipantShort')"
                                    :required="true"
                                    :state="getDegressiveState('persons', index_degresif)"
                                    :errorText="errors.degressif?.[index_degresif]?.persons"
                                    type="number"
                                    @input="handleDegressivePersonsInput(index_degresif)"
                                    classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                    :customSizeLabel="10"
                                    :customClassLabel="poppinsMedium"
                                    :floatingLabel="true"
                                    :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                />
                            </div>
                            <!-- Durée propre au palier, en liste déroulante (même composant que la
                                 durée de l'option, à l'étape Disponibilités). La première entrée,
                                 de valeur vide, retire la surcharge et rend sa durée à l'option. -->
                            <div v-if="hasDynamicDuration" class="palier-field palier-field--duration">
                                <BaseSelect
                                    v-model="price_degresif.duration"
                                    :label="$t('experience.tiers.duration')"
                                    customLabelClass="poppinsMedium"
                                    :customSizeLabel="10"
                                    compact
                                    :options="tierDurations"
                                    :searchable="false"
                                    option-label="label"
                                    option-value="value"
                                    class="transparent-select ppv2-w-full"
                                    class-added="!ppv2-rounded-lg !ppv2-h-[30px]"
                                    :floatingLabel="true"
                                    :placeholder="$t('common.select')"
                                />
                            </div>

                            <!-- « Personnalisée » (-1) : la vraie durée vit dans `personilize`. -->
                            <div
                                v-if="hasDynamicDuration && Number(price_degresif.duration) === -1"
                                class="palier-field palier-field--duration"
                            >
                                <CustomInput
                                    v-model="price_degresif.personilize"
                                    :label="$t('experience.step4.custom_duration.title')"
                                    :right-icon="$t('experience.tiers.durationUnit')"
                                    :fontSizeIconRight="10"
                                    type="number"
                                    classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                    :customSizeLabel="10"
                                    customClassLabel="poppinsMedium"
                                    :floatingLabel="true"
                                    :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                />
                            </div>
                            <div class="palier-field palier-field--price">
                                <CustomInput
                                    v-model="price_degresif.price"
                                    :label="palierPriceLabel"
                                    :right-icon="$symbolCurrency"
                                    :required="true"
                                    :state="getDegressiveState('price', index_degresif)"
                                    @input="
                                        clearDegressiveError('price', index_degresif)
                                        onPalierPriceManualEdit(index_degresif)
                                    "
                                    :errorText="errors.degressif?.[index_degresif]?.price"
                                    type="number"
                                    classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                    :customSizeLabel="10"
                                    customClassLabel="poppinsMedium"
                                    :floatingLabel="true"
                                    :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#fff'"
                                />
                            </div>
                        </div>

                        <div class="action-btns">
                            <button type="button" class="action-icon-btn" @click="duplicatePalier(index_degresif)">
                                <Copy :size="14" />
                            </button>
                            <button
                                type="button"
                                class="action-icon-btn"
                                @click="remove_degressfi_tarif(tarif.prices_degressif, index_degresif)"
                            >
                                <X :size="14" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-divider"></div>
            <div class="add-palier-row card-padding poppinsMedium" @click="$emit('duplicateDegressifTarif')">
                <Plus :size="16" />
                <span>{{ $t('experience.tiers.add') }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import BaseSelect from './BaseSelect.vue'
import { SquarePen, Plus, Minus, Copy, X, ChevronUp, ChevronDown } from 'lucide-vue'
import CustomInput from '../Inputs/CustomInput.vue'
import normalizeSize from '../../utils/normalizeSize'
export default {
    name: 'TarifItem',
    props: {
        tarif: {
            type: Object,
            required: true,
        },
        isFirst: Boolean,
        optionIndex: {
            type: Number,
            default: 1,
        },
        prestation: {
            type: Object,
            default: null,
        },
        durations: {
            type: Array,
            default: {},
        },
        // Durées proposées pour un PALIER (grille complète + « Personnalisée », sans exclusion).
        tierDurations: {
            type: Array,
            default: () => [],
        },
        // La colonne Durée n'a de sens que sur un bowling (case « durée dynamique » cochée).
        // Les colonnes Nbr. de pers. et Prix, elles, restent affichées pour tous les circuits.
        hasDynamicDuration: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        ChevronDown,
        ChevronUp,
        CustomInput,
        SquarePen,
        Plus,
        Minus,
        Copy,
        X,
        BaseSelect,
    },
    emits: ['duplicate', 'remove', 'duplicateDegressifTarif', 'focus'],
    data() {
        return {
            options_durations_experience: [],
            all_options_durations: [],
            personilize_duration: null,
            selected_interval_reservation: 10,
            selected_duration_experience: null,
            isEditingName: false,
            isDegressifOpen: false,
            palierPriceManuallyEdited: false,
            errors: {
                degressif: [],
            },
        }
    },
    computed: {
        // define hasId so code that expects it won't break
        hasId() {
            // prefer prestation.id, fallback to tarif.id if present
            return this.id !== null && this.id !== undefined
        },
        filteredOptionsDurations() {
            return this.durations
        },
        isDegressifDisabled() {
            return this.tarif.price != null && this.tarif.price !== '' && Number(this.tarif.price) === 0
        },
        /**
         * Mode tarifaire de l'expérience (étape Tarification) : true = par groupe.
         * Libellé SEULEMENT — la sémantique réelle du prix (forfait tout compris vs par
         * personne) est décidée côté serveur (PrestationService::pricesPerGroup).
         * `null` tant qu'aucun mode n'est choisi : on garde alors les libellés historiques.
         *
         * La ligne DÉFAUT est « Prix / pers. » dans les DEUX modes : hors liste des paliers,
         * c'est le prix d'option × effectif qui s'applique (décision 2026-09-11) — seuls les
         * PALIERS portent un « Prix groupe » en mode groupe.
         */
        pricesPerGroup() {
            const selections = this.$store.getters['experience/userSelections']
            if (selections?.price_per_group === 1) return true
            if (selections?.price_per_person === 1) return false
            return null
        },
        // Libellés « Prix / pers. » / « Prix groupe » : bowling seulement. Les anciens circuits
        // (booking « tarif par groupe » compris) gardent les libellés d'avant PLPRB-2652.
        optionPriceLabel() {
            if (!this.hasDynamicDuration || this.pricesPerGroup === null) return this.$t('experience.step6.labelPrice')
            return this.$t('experience.tiers.pricePerPerson')
        },
        palierPriceLabel() {
            if (!this.hasDynamicDuration) return this.$t('experience.tiers.price')
            if (this.pricesPerGroup === true) return this.$t('experience.tiers.priceGroup')
            if (this.pricesPerGroup === false) return this.$t('experience.tiers.pricePerPerson')
            return this.$t('experience.tiers.price')
        },
    },
    methods: {
        normalizeSize,
        toggleDegressif() {
            if (this.isDegressifDisabled) return

            // Toggle open/close state
            this.isDegressifOpen = !this.isDegressifOpen

            // If opening and no degressif price exists, emit to create one
            if (this.isDegressifOpen && (!this.tarif.prices_degressif || this.tarif.prices_degressif.length === 0)) {
                this.$emit('duplicateDegressifTarif')
            }
        },
        getState(field) {
            if (this.errors[field]) return 'error'

            const value = this.tarif[field]
            // Proper validation based on field type
            if (field === 'price') {
                if (value != null && value !== '' && !isNaN(Number(value)) && Number(value) >= 0) return 'valid'
            } else if (field === 'duree') {
                const numValue = Number(value)
                if (!isNaN(numValue) && numValue > 0) return 'valid'
            } else if (value !== null && value !== undefined && value !== '') {
                return 'valid'
            }

            return 'default'
        },

        getDegressiveState(field, index) {
            if (this.errors.degressif?.[index]?.[field]) return 'error'

            const value = this.tarif.prices_degressif?.[index]?.[field]
            // Proper validation for degressif fields (persons and price must be > 0)
            if (field === 'persons' || field === 'price') {
                const numValue = Number(value)
                if (!isNaN(numValue) && numValue > 0) return 'valid'
            } else if (value !== null && value !== undefined && value !== '') {
                return 'valid'
            }

            return 'default'
        },

        clearError(field) {
            if (this.errors[field]) {
                this.$delete(this.errors, field)
            }
        },

        clearDegressiveError(field, index) {
            if (this.errors.degressif?.[index]?.[field]) {
                this.$delete(this.errors.degressif[index], field)
            }
        },
        handleDegressivePersonsInput(index) {
            // Clear existing error for this field, then re-validate duplicates only
            this.clearDegressiveError('persons', index)
            this.validateDegressivePersonsLive()
        },
        /**
         * La durée d'un palier est obligatoire — mais UNIQUEMENT là où le champ est affiché,
         * c'est-à-dire sur une expérience à durée dynamique. Sans cette condition, les paliers
         * purement tarifaires de vr/paintball/booking (durée héritée de l'option, donc vide à
         * l'écran) deviendraient invalides et bloqueraient l'enregistrement.
         *
         * Même règle qu'à l'étape Disponibilités : ce qui passe d'un côté doit passer de l'autre.
         */
        validateDegressiveDurationsLive() {
            const prices = this.tarif?.prices_degressif || []

            if (!Array.isArray(this.errors.degressif)) {
                this.$set(this.errors, 'degressif', [])
            }

            prices.forEach((deg, idx) => {
                if (!this.errors.degressif[idx]) this.$set(this.errors.degressif, idx, {})

                const message = this.tierDurationError(deg)

                if (message) {
                    this.$set(this.errors.degressif[idx], 'duration', message)
                } else {
                    this.$delete(this.errors.degressif[idx], 'duration')
                }
            })
        },
        /**
         * UNIQUE règle de validité de la durée d'un palier — partagée par le contrôle live et par
         * validateTarifs (la porte de sauvegarde). Elles avaient divergé : la porte rejetait le
         * marqueur « Personnalisée » (-1) que le live résolvait via `personilize`, donc un bowling
         * aux durées hors grille (28, 36 min) ne pouvait plus enregistrer la Tarification.
         *
         * Durée dynamique active : requise. Sinon : facultative (durée héritée de l'option, champ
         * non affiché), mais valide si présente. Même règle que le serveur (Step5Request).
         *
         * @returns {string|null} message d'erreur, ou null si valide
         */
        tierDurationError(deg) {
            // Hors bowling le champ n'est pas affiché : aucune règle, comme avant PLPRB-2652
            // (un palier historique chargé avec une durée 0 ne doit pas bloquer en silence).
            if (!this.hasDynamicDuration) return null

            const selected = deg?.duration

            if (selected === '' || selected === null || selected === undefined) {
                return this.$t('experience.tiers.durationRequired')
            }

            const value = Number(selected) === -1 ? Number(deg?.personilize) : Number(selected)

            return Number.isFinite(value) && value > 0 ? null : this.$t('experience.tiers.durationInvalid')
        },
        /**
         * Un prix est manquant s'il est vide ou non numérique. Pour un PALIER (`strict`), 0 compte
         * aussi comme manquant : c'est la représentation « non renseigné » (ce que l'étape
         * Disponibilités écrit, ce que resolveTier ignore, ce que le chargeur ramène à ''). Le prix
         * de l'option, lui, peut valoir 0. Même règle que le serveur (Step5Request : `gt:0`).
         */
        isMissingPrice(value, strict = false) {
            if (value === null || value === undefined || value === '') return true
            const number = Number(value)
            if (!Number.isFinite(number) || number < 0) return true
            return strict && number <= 0
        },
        validateDegressivePersonsLive() {
            const prices = this.tarif?.prices_degressif || []

            if (!Array.isArray(prices)) return

            this.validateDegressiveDurationsLive()

            // Ensure degressif errors array exists
            if (!Array.isArray(this.errors.degressif)) {
                this.$set(this.errors, 'degressif', [])
            }

            // Message imposé par le ticket : « Un palier existe déjà pour X personnes (palier N) ».
            const duplicateMsgFor = (persons, palier) => this.$t('experience.tiers.duplicatePersons', { persons, palier })

            // Clear previous duplicate errors for persons only
            prices.forEach((_, idx) => {
                if (this.errors.degressif?.[idx]?.isDuplicatePersons) {
                    this.$delete(this.errors.degressif[idx], 'persons')
                    this.$delete(this.errors.degressif[idx], 'isDuplicatePersons')
                }
            })

            const seen = new Map()

            prices.forEach((deg, idx) => {
                const val = Number(deg.persons)
                if (!val) return

                if (seen.has(val)) {
                    const firstIdx = seen.get(val)

                    // Seule la saisie EN DOUBLE est signalée : le palier d'origine reste valide,
                    // c'est le second qui doit changer.
                    const curErrors = this.errors.degressif[idx] || {}
                    curErrors.persons = duplicateMsgFor(val, firstIdx + 1)
                    curErrors.isDuplicatePersons = true
                    this.$set(this.errors.degressif, idx, curErrors)
                } else {
                    seen.set(val, idx)
                }
            })
        },
        clearCustomDuration() {
            const first = this.filteredOptionsDurations.length > 0 ? this.filteredOptionsDurations[0].value : null
            this.selected_duration_experience = first
        },
        duplicatePalier(index) {
            const source = this.tarif.prices_degressif[index]
            // La duplication reprend la durée ET le prix (demande du ticket), mais pas le nombre de
            // personnes : le recopier tel quel créait un doublon immédiatement en erreur. On prend
            // donc le premier effectif libre au-dessus du plus grand palier existant.
            const copy = { ...source, persons: this.nextFreePalierPersons() }
            this.tarif.prices_degressif.splice(index + 1, 0, copy)
            this.validateDegressivePersonsLive()
        },
        /**
         * Premier nombre de personnes non encore utilisé par un palier de cette option.
         */
        nextFreePalierPersons() {
            const used = new Set(
                (this.tarif?.prices_degressif || []).map((deg) => Number(deg.persons)).filter((value) => value > 0)
            )

            let candidate = used.size > 0 ? Math.max(...used) + 1 : 1
            while (used.has(candidate)) {
                candidate += 1
            }

            return candidate
        },
        remove_degressfi_tarif(tarifs_degressif, index) {
            tarifs_degressif.splice(index, 1)
            // Mettre à jour les erreurs
            if (this.errors.degressif?.[index]) {
                this.errors.degressif.splice(index, 1)
            }
        },
        /**
         * Mark palier 1 as manually edited so the watcher stops overwriting it.
         */
        onPalierPriceManualEdit(index) {
            if (index === 0) {
                this.palierPriceManuallyEdited = true
            }
        },
        validateTarifs() {
            this.errors = { degressif: [] }

            const ta = this.tarif || {}

            if (!ta.duree) this.errors.duree = this.$t('experience.errors.requiredDuration')
            if (this.isMissingPrice(ta.price)) this.errors.price = this.$t('experience.errors.requiredPrice')

            if (Array.isArray(ta.prices_degressif)) {
                const seenPersons = new Map()

                ta.prices_degressif.forEach((deg, index) => {
                    const degErrors = {}
                    const personsValue = deg.persons

                    if (!personsValue) {
                        degErrors.persons = this.$t('experience.errors.requiredNbrParticipant')
                    } else {
                        const key = Number(personsValue)
                        if (seenPersons.has(key)) {
                            // Erreur BLOQUANTE : deux paliers visant le même effectif rendraient la
                            // résolution (« le plus grand nb <= N ») non déterministe.
                            degErrors.persons = this.$t('experience.tiers.duplicatePersons', {
                                persons: key,
                                palier: seenPersons.get(key) + 1,
                            })
                        } else {
                            seenPersons.set(key, index)
                        }
                    }

                    // Même règle que le contrôle live (voir tierDurationError).
                    const durationError = this.tierDurationError(deg)
                    if (durationError) degErrors.duration = durationError

                    // « Prix* » : requis à la Tarification et > 0 — bowling seulement, comme le serveur
                    // (Step5Request). Hors bowling le prix d'un palier reste facultatif (règle d'avant).
                    if (this.hasDynamicDuration && this.isMissingPrice(deg.price, true)) {
                        degErrors.price = this.$t('experience.errors.requiredPrice')
                    }

                    this.$set(this.errors.degressif, index, degErrors)
                })
            }

            return (
                !this.errors.name &&
                !this.errors.duree &&
                !this.errors.price &&
                this.errors.degressif.every((err) => Object.keys(err).length === 0)
            )
        },
        initializeSelectedDuration() {
            const duree = Number(this.tarif.duree)
            // New tarif (no duree set) → default to first available duration
            if (!duree || duree === 0) {
                const first = this.filteredOptionsDurations.length > 0 ? this.filteredOptionsDurations[0].value : null
                this.selected_duration_experience = first
                return
            }
            const exists = this.filteredOptionsDurations.some((opt) => Number(opt.value) === duree)
            this.selected_duration_experience = exists ? duree : -1
        },
    },
    mounted() {
        // Auto-open degressif section if prices_degressif already exist
        if (this.tarif.prices_degressif && this.tarif.prices_degressif.length > 0) {
            this.isDegressifOpen = true
            // In edit mode, only protect palier 1 from auto-fill if it already
            // has a meaningful price from the DB. If the price is empty, let the
            // tarif.price watcher auto-fill it.
            const firstPrice = this.tarif.prices_degressif[0]?.price
            if (firstPrice != null && firstPrice !== '' && parseFloat(firstPrice) >= 0) {
                this.palierPriceManuallyEdited = true
            }
        } else if (this.isDegressifOpen && (!this.tarif.prices_degressif || this.tarif.prices_degressif.length === 0)) {
            // If open by default but empty, create the first item
            this.$emit('duplicateDegressifTarif')
        }
    },
    watch: {
        durations: {
            handler() {
                this.initializeSelectedDuration()
            },
            immediate: true,
            deep: true,
        },
        selected_duration_experience(newVal) {
            if (Number(newVal) === -1) {
                const values = this.filteredOptionsDurations.map((opt) => opt.value)
                this.tarif.duree = Math.max(...values) + 5
            } else {
                this.tarif.duree = newVal
            }
        },
        // ✅ FIX: Auto-open degressif section when component becomes first (only if prices already exist)
        isFirst: {
            handler(newVal) {
                if (newVal && this.tarif.prices_degressif && this.tarif.prices_degressif.length > 0) {
                    this.isDegressifOpen = true
                }
            },
            immediate: true,
        },
        // ✅ FIX: Watch tarif.price and sync to Palier 1 (unless manually edited)
        // Also auto-close degressif section when price is 0
        'tarif.price': {
            handler(newVal) {
                // Auto-close degressif when price is 0
                if (newVal != null && newVal !== '' && Number(newVal) === 0) {
                    this.isDegressifOpen = false
                }

                if (
                    !this.palierPriceManuallyEdited &&
                    newVal &&
                    Array.isArray(this.tarif.prices_degressif) &&
                    this.tarif.prices_degressif.length > 0
                ) {
                    this.$set(this.tarif.prices_degressif[0], 'price', newVal)
                }
            },
        },
        // ✅ FIX: Re-create first palier when prices_degressif becomes empty after prop update
        // This handles the case where API data arrives in edit mode and overwrites the
        // palier that was auto-created in mounted() with an empty array.
        // Also auto-fills Palier 1 price from the main tarif price when a new palier is created.
        'tarif.prices_degressif': {
            handler(newVal) {
                if (this.isDegressifOpen && (!newVal || newVal.length === 0)) {
                    this.$nextTick(() => {
                        this.$emit('duplicateDegressifTarif')
                    })
                } else if (
                    !this.palierPriceManuallyEdited &&
                    newVal &&
                    newVal.length > 0 &&
                    !newVal[0].price &&
                    this.tarif.price
                ) {
                    // Palier 1 just appeared with no price but main tarif has one → sync
                    this.$set(newVal[0], 'price', this.tarif.price)
                }
            },
        },
    },
}
</script>

<style scoped>
/* ── Card ── */
.option-card {
    border-radius: 14px;
    border: 1px solid #e2e2e2;
    background: #fff;
    margin-bottom: 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    position: relative;
}
.dark .option-card {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: none;
}

.card-padding {
    padding: 12px 14px;
}

.card-divider {
    height: 1px;
    background: #efefef;
}
.dark .card-divider {
    background: rgba(255, 255, 255, 0.08);
}

/* ── Top row: stacks name-col left, inputs-row right ── */
.option-top-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

/* Name column — fixed width, never grows */
.option-name-col {
    flex: 0 0 auto;
    max-width: 120px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 2px;
    overflow: hidden;
}

/* Inputs row — takes all remaining space */
.option-inputs-row {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

/* Input group holds duration + custom + price */
.input-group {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    align-items: flex-start;
}

.input-item {
    min-width: 0;
}
.input-item--duration {
    flex: 1.5 1 0;
}
.input-item--price {
    flex: 1 1 0;
}

/* ── Custom duration wrapper ── */
.custom-duration-wrapper {
    position: relative;
    width: 100%;
}
.clear-custom-btn {
    position: absolute;
    right: 4px;
    bottom: 5px;
    width: 20px;
    height: 20px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: opacity 0.15s ease;
    padding: 0;
    z-index: 2;
    opacity: 0.85;
}
.clear-custom-btn:hover {
    opacity: 1;
}
.dark .clear-custom-btn {
    opacity: 0.9;
}
.dark .clear-custom-btn:hover {
    opacity: 1;
}

/* ── Action buttons ── */
.action-btns {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    flex-shrink: 0;
    padding-top: 8px;
}

.action-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid #d5d5d5;
    background: #fff;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
    transition: all 0.15s ease;
}
.action-icon-btn:hover {
    background: #f4f4f4;
    border-color: #aaa;
    color: rgba(0, 0, 0, 0.75);
}
.dark .action-icon-btn {
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.5);
    border-color: rgba(255, 255, 255, 0.14);
}
.dark .action-icon-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.85);
    border-color: rgba(255, 255, 255, 0.28);
}

/* ── Palier rows ── */
.palier-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.palier-label {
    flex: 0 0 120px;
    max-width: 120px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
    white-space: nowrap;
    padding-top: 12px;
    display: flex;
    align-items: center;
    min-width: 0;
}
.dark .palier-label {
    color: rgba(255, 255, 255, 0.45);
}

/* Même rappel qu'à l'étape Disponibilités (.tier-hint de Step4.vue). */
.palier-fallback-hint {
    font-family: 'Poppins Regular', sans-serif;
    font-size: 11px;
    opacity: 0.65;
    margin: 10px 0 0;
}

.palier-inputs-row {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.palier-inputs {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-wrap: nowrap;
    gap: 6px;
    align-items: flex-start;
}
.palier-field {
    min-width: 0;
}
.palier-field--duration {
    flex: 1.5 1 0;
}
.palier-field--price {
    flex: 1 1 0;
}

/* ── Add palier link ── */
.add-palier-row {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #7a7ab8;
    font-size: 12px;
    transition: color 0.15s;
}
.add-palier-row:hover {
    color: #545595;
}
.dark .add-palier-row {
    color: #9090cc;
}
.dark .add-palier-row:hover {
    color: #b0b0e0;
}

/* ── Editable name ── */
.editable-label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    gap: 4px;
}

.edit-pen-icon {
    cursor: pointer;
    opacity: 0.35;
    flex-shrink: 0;
    transition: opacity 0.15s;
}
.editable-label:hover .edit-pen-icon {
    opacity: 0.7;
}

.editable-input {
    width: 100%;
}

.label_name_tarif {
    font-family: 'Poppins SemiBold', 'Poppins Medium', sans-serif;
    font-size: 12px;
    line-height: 1;
    color: #1a1a2e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
}
.dark .label_name_tarif {
    color: #e0e0f0;
}

.degressif-link {
    font-family: 'Poppins Regular', sans-serif;
    font-size: 10px;
    color: #7272a8;
    text-decoration: underline;
    cursor: pointer;
    line-height: 1.3;
    transition: color 0.15s;
}
.degressif-link:hover {
    color: #4a4a90;
}
.degressif-link--disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
}
.dark .degressif-link {
    color: #9898c0;
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .option-top-row {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }
    .option-name-col {
        width: 100%;
        flex-direction: row;
        align-items: center;
        gap: 12px;
    }
    .input-group {
        flex-wrap: wrap;
    }
    .palier-row {
        flex-wrap: wrap;
    }
    .palier-label {
        flex-basis: 100%;
    }
}

/* ── BaseSelect overrides ── */
::v-deep .custom-select-height {
    height: 30px !important;
    min-height: 30px !important;
    border-radius: 4px !important;
}
::v-deep .custom-select-height .vs__dropdown-toggle {
    height: 30px !important;
    min-height: 30px !important;
    border: none !important;
    padding: 0 !important;
    background-color: transparent !important;
    display: flex;
    align-items: center;
}
::v-deep .custom-select-height .vs__selected {
    margin: 0 !important;
    padding: 0 12px !important;
    color: #021832 !important;
    line-height: normal !important;
    border: none !important;
    background: transparent !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
::v-deep .custom-select-height .vs__search {
    display: none !important;
}
::v-deep .custom-select-height .vs__actions {
    padding: 0 8px !important;
    margin: 0 !important;
}
::v-deep .custom-select-height .vs__open-indicator {
    fill: #000 !important;
}

.dark ::v-deep .custom-select-height {
    border-color: #9c9af880 !important;
}
.dark ::v-deep .custom-select-height .vs__selected,
.dark ::v-deep .custom-select-height .vs__search {
    color: #fff !important;
}
.dark ::v-deep .custom-select-height .vs__open-indicator {
    fill: #fff !important;
}

::v-deep .vs__dropdown-menu {
    z-index: 9999 !important;
}
</style>
